import { useMemo } from "react";
import type { GameView, PublicUser } from "@gierki/shared";
import { gameName } from "@gierki/shared";
import { useStore } from "../store.ts";
import { useCountdown, toSeconds, useOnChange, vibrate } from "../hooks.ts";
import { Ekran, Komunikat, Polaczenie, Przycisk } from "../ui.tsx";
import { PanstwaMiasta, pmNaglowek, type PmView } from "../game/PanstwaMiasta.tsx";
import { Kosci, type KosciView } from "../game/Kosci.tsx";
import { Scrabble, type ScrabbleView } from "../game/Scrabble.tsx";
import { Catan, type CatanView } from "../game/Catan.tsx";
import { Superfarmer, type SuperfarmerView } from "../game/Superfarmer.tsx";
import { Statki, type StatkiView } from "../game/Statki.tsx";
import { Perudo, type PerudoView } from "../game/Perudo.tsx";
import { Makao, type MakaoView } from "../game/Makao.tsx";
import { Literaki, type LiterakiView } from "../game/Literaki.tsx";
import { Wordle, type WordleView } from "../game/Wordle.tsx";

/**
 * Rozdzielnik gier.
 *
 * Wspólne dla wszystkich jest to, co i tak zapewnia silnik: pasek tytułu,
 * odliczanie do deadline'u, wskaźnik połączenia, obsługa zamknięcia partii
 * i wysyłka akcji. Reguły konkretnej gry siedzą w `web/src/game/`.
 *
 * Dołożenie kolejnej gry to jeden `case` tutaj plus jej ekrany.
 */
export function Game({ view }: { view: GameView }) {
  const { user, status, sendAction, leaveGame, clearView, closed, showToast } = useStore();

  const left = useCountdown(view.deadlineAt);
  const sekundy = toSeconds(left);

  const nicki = useMemo(
    () => Object.fromEntries(view.players.map((player: PublicUser) => [player.id, player.nick])),
    [view.players],
  );

  /*
   * `phase` albo `faza`: nowsze gry nazywają to pole po polsku (patrz „Wspólne
   * nazwy w pluginach gier" w CLAUDE.md), starsze zostały przy angielskim.
   * Rozdzielnik pyta o jedno i o drugie, żeby żadna z nich nie straciła
   * haptyki i ukrywania odliczania w lobby.
   */
  const widok = view.view as { phase?: string; faza?: string };
  const faza = widok.phase ?? widok.faza ?? "";

  // Haptyka na zmianę fazy: telefon bywa w uchwycie albo w kieszeni, a start
  // rundy i moment STOP to rzeczy, których nie wolno przegapić.
  useOnChange(faza, (nowa) => {
    if (nowa === "round" || nowa === "runda" || nowa === "tura") vibrate([40, 60, 40]);
    else if (nowa === "objections") vibrate(120);
  });

  const wykonaj = (action: unknown) => {
    void sendAction(view.id, action).catch((error: unknown) => {
      showToast(error instanceof Error ? error.message : "Nie udało się.");
    });
  };

  /*
   * „Zakończona" to nie to samo, co „zamknięta".
   *
   * Przy normalnym końcu partii serwer przysyła jeszcze ostatni widok z fazą
   * końcową — i to jest ekran wyników, na który wszyscy czekali. Ekran „koniec"
   * jest tylko dla przypadków, w których wyników nie ma.
   */
  if (closed?.gameId === view.id && closed.reason !== "finished") {
    return <Zamknieta powod={closed.reason} onWroc={clearView} />;
  }

  const mojeId = user?.id ?? "";
  const tytul =
    view.gameType === "panstwa-miasta"
      ? pmNaglowek(view.view as PmView)
      : gameName(view.gameType);

  const podtytul =
    sekundy !== null && faza !== "lobby" && faza !== "results" ? (
      <span className={sekundy <= 10 ? "text-blad tabular" : "tabular"}>
        {Math.floor(sekundy / 60)}:{String(sekundy % 60).padStart(2, "0")}
      </span>
    ) : (
      view.players.map((player) => player.nick).join(", ")
    );

  return (
    <Ekran
      tytul={tytul}
      podtytul={podtytul}
      akcja={
        <Przycisk wariant="cichy" onClick={clearView}>
          Wróć
        </Przycisk>
      }
    >
      <Polaczenie status={status} />

      {view.gameType === "panstwa-miasta" ? (
        <PanstwaMiasta
          view={view}
          pm={view.view as PmView}
          nicki={nicki}
          mojeId={mojeId}
          wykonaj={wykonaj}
          onWyjdz={() => void leaveGame(view.id)}
          onWroc={clearView}
        />
      ) : view.gameType === "scrabble" ? (
        <Scrabble
          view={view.view as ScrabbleView}
          nicki={nicki}
          mojeId={mojeId}
          onStart={() => wykonaj({ type: "start" })}
          onZagraj={(placements) => wykonaj({ type: "play", placements })}
          onPas={() => wykonaj({ type: "pass" })}
          onWymiana={(tiles) => wykonaj({ type: "exchange", tiles })}
          onSprzeciw={(on) => wykonaj({ type: "object", on })}
        />
      ) : view.gameType === "kosci" ? (
        <Kosci
          view={view.view as KosciView}
          nicki={nicki}
          mojeId={mojeId}
          onStart={() => wykonaj({ type: "start" })}
          onRzut={() => wykonaj({ type: "roll" })}
          onZatrzymaj={(index) => wykonaj({ type: "hold", index })}
          onZapisz={(category) => wykonaj({ type: "score", category })}
        />
      ) : view.gameType === "catan" ? (
        <Catan
          view={view.view as CatanView}
          nicki={nicki}
          mojeId={mojeId}
          onStart={() => wykonaj({ type: "start" })}
          onOsada={(w) => wykonaj({ type: "postaw-osade", w })}
          onDroga={(k) => wykonaj({ type: "postaw-droge", k })}
          onRzut={() => wykonaj({ type: "rzuc" })}
          onBuduj={(co, gdzie) => wykonaj({ type: "buduj", co, gdzie })}
          onHandel={(oddaje, bierze) => wykonaj({ type: "handel-bank", oddaje, bierze })}
          onKoniecTury={() => wykonaj({ type: "koniec-tury" })}
          onOdrzuc={(karty) => wykonaj({ type: "odrzuc", karty })}
          onZlodziej={(heks, ofiara) => wykonaj({ type: "zlodziej", heks, ofiara })}
          onKupRozwoj={() => wykonaj({ type: "kup-rozwoj" })}
          onZagrajRozwoj={(karta, opcje) => wykonaj({ type: "zagraj-rozwoj", karta, ...opcje })}
          onPrzyjmij={() => wykonaj({ type: "przyjmij" })}
        />
      ) : view.gameType === "superfarmer" ? (
        <Superfarmer
          view={view.view as SuperfarmerView}
          nicki={nicki}
          mojeId={mojeId}
          onStart={() => wykonaj({ type: "start" })}
          onRzut={() => wykonaj({ type: "rzut" })}
          onWymiana={(kurs, razy, odwrotnie) => wykonaj({ type: "wymiana", kurs, razy, odwrotnie })}
          onPropozycja={(komu, kurs, razy, odwrotnie) =>
            wykonaj({ type: "propozycja", komu, kurs, razy, odwrotnie })
          }
          onOdpowiedz={(akceptuj) => wykonaj({ type: "odpowiedz", akceptuj })}
        />
      ) : view.gameType === "perudo" ? (
        <Perudo
          view={view.view as PerudoView}
          nicki={nicki}
          mojeId={mojeId}
          onStart={() => wykonaj({ type: "start" })}
          onZaklad={(ile, oczko) => wykonaj({ type: "zaklad", ile, oczko })}
          onDudo={() => wykonaj({ type: "dudo" })}
          onCalza={() => wykonaj({ type: "calza" })}
          onDalej={() => wykonaj({ type: "dalej" })}
        />
      ) : view.gameType === "makao" ? (
        <Makao
          view={view.view as MakaoView}
          nicki={nicki}
          mojeId={mojeId}
          onStart={() => wykonaj({ type: "start" })}
          onZagraj={(karta) => wykonaj({ type: "zagraj", karta })}
          onZadajKolor={(kolor) => wykonaj({ type: "zazadaj-koloru", kolor })}
          onZadajFigure={(figura) => wykonaj({ type: "zazadaj-figury", figura })}
          onDobierz={() => wykonaj({ type: "dobierz" })}
          onPas={() => wykonaj({ type: "pas" })}
          onMakao={() => wykonaj({ type: "makao" })}
          onLapy={(na) => wykonaj({ type: "lapy", na })}
        />
      ) : view.gameType === "statki" ? (
        <Statki
          view={view.view as StatkiView}
          nicki={nicki}
          mojeId={mojeId}
          onStart={() => wykonaj({ type: "start" })}
          onPostaw={(rozmiar, pole, pionowo) =>
            wykonaj({ type: "postaw", rozmiar, pole, pionowo })
          }
          onZdejmij={(pole) => wykonaj({ type: "zdejmij", pole })}
          onLosuj={() => wykonaj({ type: "rozstaw-losowo" })}
          onGotow={() => wykonaj({ type: "gotow" })}
          onCofnijGotow={() => wykonaj({ type: "cofnij-gotow" })}
          onStrzel={(cel, pole) => wykonaj({ type: "strzel", cel, pole })}
          onCofnijStrzal={() => wykonaj({ type: "cofnij-strzal" })}
        />
      ) : view.gameType === "wordle" ? (
        <Wordle
          view={view.view as WordleView}
          nicki={nicki}
          mojeId={mojeId}
          onStart={() => wykonaj({ type: "startRound" })}
          onZgadnij={(slowo) => wykonaj({ type: "zgadnij", slowo })}
          onPoddaj={() => wykonaj({ type: "poddaj" })}
          onWroc={clearView}
        />
      ) : view.gameType === "literaki" ? (
        <Literaki
          view={view.view as LiterakiView}
          nicki={nicki}
          mojeId={mojeId}
          onStart={() => wykonaj({ type: "startRound" })}
          onSlowo={(slowo) => wykonaj({ type: "slowo", slowo })}
          onGotowe={(on) => wykonaj({ type: "gotowe", on })}
          onWroc={clearView}
        />
      ) : (
        <Komunikat rodzaj="blad">
          Ta wersja aplikacji nie zna gry „{view.gameType}". Odśwież stronę.
        </Komunikat>
      )}
    </Ekran>
  );
}

function Zamknieta({ powod, onWroc }: { powod: string; onWroc: () => void }) {
  const opisy: Record<string, string> = {
    left: "Ktoś wyszedł z partii, więc została przerwana.",
    "state-version-mismatch":
      "Ta partia była na starszej wersji gry i nie da się jej dokończyć. Zacznijcie nową.",
    corrupted: "Nie udało się wczytać tej partii.",
  };

  return (
    <Ekran tytul="Koniec">
      <Komunikat>{opisy[powod] ?? "Partia została zamknięta."}</Komunikat>
      <Przycisk wariant="glowny" pelna onClick={onWroc}>
        Wróć do listy
      </Przycisk>
    </Ekran>
  );
}
